import { algoliaClient, parseColorsFromAlgoliaNamedTags, waitForAlgoliaClient } from './algolia/utils';
import { handleCardHover, handleSwatchClick, productCardTemplate } from './algolia/widgets/search-dropdown/product-card';

(async () => {
  // Required for Instantsearch.js
  window.process = {
    env: {
      NODE_ENV: 'production',
    },
  };

  // Checks if the algolia config has loaded before continuing
  try {
    await waitForAlgoliaClient();
  } catch (error) {
    console.error('Error while waiting for algolia:', error);
    return;
  }

  const { banners, products, relatedResults, searchInput } = await import('./algolia/widgets/search-dropdown');
  const { default: Cookies } = await import('js-cookie');

  class SearchDropdown extends HTMLElement {
    constructor() {
      super();

      this._visibility = false;

      this.algoliaConfig = window.algoliaShopify.config;

      this.siteHeader = document.querySelector('.shopify-section-header');
      this.headerSearchInput = document.querySelector('#headerSearchInput');
      this.headerSearchResetBtn = document.getElementById('headerSearchResetBtn');
      this.panelCloseBtn = this.querySelector('.search-dropdown__close');
    }

    async connectedCallback() {
      this.initInputPlaceholder();

      this.client = algoliaClient(this.algoliaConfig.app_id, this.algoliaConfig.search_api_key);

      this.search = window.instantsearch({
        indexName: `${this.algoliaConfig.index_prefix}products`,
        insights: {
          insightsInitParams: {
            useCookie: true,
          },
        },
        searchClient: this.client,
        searchFunction: (helper) => {
          // Don't make the search request until at least 2 characters entered
          if (helper.state.query && helper.state.query.length >= 3) {
            helper.search();
          }

          return false;
        },
      });

      // Sort color maps
      this.colorsMap = this.dataset.colours
        ? this.dataset.colours.split(',').reduce((colors, colorStr) => {
            const parts = colorStr.split('|');
            if (parts[1]) {
              colors[parts[0]] = parts[1];
            }
            return colors;
          }, {})
        : {};

      // Each section used in the dropdown
      this.sections = {};
      this.sectionIds = ['products', 'searches', 'collections', 'articles'];

      this.loadPlaceholderProducts();

      this.attachListeners();

      this.addWidgets();

      this.search.start();
    }

    initInputPlaceholder() {
      if (this.dataset.inputPlaceholders === '') return;

      const strings = this.dataset.inputPlaceholders.split('~');

      const typeWriter = (text, i, callback) => {
        if (i < text.length) {
          this.headerSearchInput.placeholder += text.charAt(i);
          i++;
          setTimeout(() => typeWriter(text, i, callback), 50);
        } else {
          setTimeout(() => deleteText(callback), 1000);
        }
      };

      const deleteText = (callback) => {
        const currentText = this.headerSearchInput.placeholder;

        if (currentText.length > 0) {
          this.headerSearchInput.placeholder = currentText.slice(0, -1);
          setTimeout(() => deleteText(callback), 50);
        } else {
          setTimeout(callback, 1000);
        }
      };

      const typeStrings = (index) => {
        if (index < strings.length) {
          typeWriter(strings[index], 0, () => {
            typeStrings(index + 1);
          });
        } else {
          setTimeout(() => typeStrings(0), 2000);
        }
      };

      typeStrings(0);
    }

    async loadPlaceholderProducts() {
      // Helper function to store data with an expiry
      const setItemWithExpiry = (key, value, ttl) => {
        const now = new Date();
        const item = {
          value: value,
          expiry: now.getTime() + ttl,
        };
        localStorage.setItem(key, JSON.stringify(item));
      };

      // Helper function to get data, checking for expiry
      const getItemWithExpiry = (key) => {
        const itemStr = localStorage.getItem(key);
        if (!itemStr) {
          return null;
        }
        const item = JSON.parse(itemStr);
        const now = new Date();
        if (now.getTime() > item.expiry) {
          localStorage.removeItem(key);
          return null;
        }
        return item.value;
      };

      const storedProducts = getItemWithExpiry('search-dropdown-products');

      if (storedProducts) {
        this.insertPlaceholderProducts(storedProducts);

        return;
      }

      const index = this.client.initIndex(`${this.algoliaConfig.index_prefix}products`);

      const products = await index
        .search('', {
          analytics: false,
          attributesToHighlight: [],
          attributesToRetrieve: ['title', 'image', 'named_tags', 'price', 'id', 'compare_at_price', 'price_ratio', 'variants_inventory_count', 'handle', 'position', 'vendor', 'meta'],
          clickAnalytics: true,
          filters: `collection_ids:"${this.dataset.collectionId}" AND inventory_quantity > 0`,
          hitsPerPage: 8,
          responseFields: ['hits'],
          ruleContexts: ['search-dropdown-products'],
        })
        .then(({ hits, queryID }) =>
          hits.map((product) => {
            const colors = parseColorsFromAlgoliaNamedTags(product, this.colorsMap);
            return {
              ...product,
              _distinct: true,
              can_order: product.inventory_management !== 'shopify' || product.inventory_policy === 'continue' || product.inventory_quantity > 0,
              queryID: queryID,
              productPosition: product.position,
              index: `${this.algoliaConfig.index_prefix}products`,
              variation_title: product.meta?.variation?.title || '',
              colors,
            };
          })
        );

      // Store the products with a 7-day expiry (7 days * 24 hours * 60 minutes * 60 seconds * 1000 milliseconds)
      setItemWithExpiry('search-dropdown-products', products, 7 * 24 * 60 * 60 * 1000);

      this.insertPlaceholderProducts(products);
    }

    insertPlaceholderProducts(products) {
      const listEl = this.querySelector('.search-dropdown__items-list');

      listEl.innerHTML = products
        .map(
          (product) => `
          <li class="search-dropdown__item is-col-6 is-col-4-md is-col-3-xl">
            ${productCardTemplate(product)}
          </li>
        `
        )
        .join('');

      const allProductCards = listEl.querySelectorAll('[data-product-card]');

      allProductCards.forEach((card) => {
        // Card hover events
        const hoverElem = card.querySelector('[data-product-card-inner]');

        hoverElem.removeEventListener('mouseenter', handleCardHover);
        hoverElem.addEventListener('mouseenter', handleCardHover, {
          once: true,
        });

        // Swatches click events
        const cardSwatches = card.querySelectorAll('[data-product-card-color-id]');

        cardSwatches.forEach((swatch) => {
          swatch.removeEventListener('click', handleSwatchClick);
          swatch.addEventListener('click', handleSwatchClick);
        });

        if (card.querySelector('[data-show-remaining-colours]')) {
          card.querySelector('[data-show-remaining-colours]').addEventListener('click', (e) => {
            e.target.remove();

            card.querySelectorAll('.product-card-colors__link--hidden').forEach((el) => {
              el.classList.remove('product-card-colors__link--hidden');
            });
          });
        }
      });

      // Store the sections (placeholder ones on load)
      this.saveSections();
    }

    attachListeners() {
      // On focus of search input
      this.headerSearchInput.addEventListener('focus', this.open.bind(this));

      // On click of search dropdown close icon
      this.panelCloseBtn.addEventListener('click', this.close.bind(this));

      // Attach window resize listener to reposition dropdown
      window.addEventListener('resize', this.readjust.bind(this));
    }

    addWidgets() {
      // Config
      const config = {
        attributesToHighlight: [],
        attributesToRetrieve: ['title', 'image', 'named_tags', 'price', 'id', 'compare_at_price', 'price_ratio', 'variants_inventory_count', 'handle', 'vendor', 'meta'],
        clickAnalytics: true,
        hitsPerPage: 8,
      };

      let stockPolicy = false;

      if (this.algoliaConfig.stock_policy) {
        if (this.algoliaConfig.stock_policy === 'deny') {
          stockPolicy = 'inventory_quantity > 0';
        } else if (this.algoliaConfig.stock_policy === 'continue') {
          stockPolicy = 'inventory_available:true';
        }
      }

      // Add the stock policy to config
      if (stockPolicy) {
        config.filters = stockPolicy;
      }

      config.analytics = true;
      config.analyticsTags = ['searchBar'];

      /**
       * Set the user token, this uses the Shopify Customer ID if logged in,
       * otherwise the anonymous token algolia creates and stores as a cookie is used
       */
      config.userToken = window.algolia.customer ? window.algolia.customer : Cookies.get('_ALGOLIA');

      this.search.addWidgets([window.instantsearch.widgets.configure(config)]);

      // Search Input
      let timerId;

      this.search.addWidgets([
        searchInput({
          container: this.headerSearchInput,
          dropdown: this,
          queryHook(query, refine) {
            // This prevents algolia making a search request per keystroke and instead waits 500ms after typing
            clearTimeout(timerId);
            timerId = setTimeout(() => refine(query), 500);
          },
        }),
      ]);

      // Products

      // this.insertPlaceholderColours(colorsMap);

      this.search.addWidgets([
        products({
          container: this.querySelector('[data-source-section="products"]'),
          transformItems: (products) =>
            products.map((product) => {
              const colors = parseColorsFromAlgoliaNamedTags(product, this.colorsMap);

              return {
                ...product,
                _distinct: Boolean(this.algoliaConfig.show_products),
                can_order: product.inventory_management !== 'shopify' || product.inventory_policy === 'continue' || product.inventory_quantity > 0,
                queryID: product.__queryID,
                productPosition: product.__position,
                index: `${this.algoliaConfig.index_prefix}products`,
                colors,
              };
            }),
        }),
      ]);

      // Searches
      this.search.addWidgets([
        window.instantsearch.widgets
          .index({
            indexName: `${this.algoliaConfig.index_prefix}products_query_suggestions`,
          })
          .addWidgets([
            window.instantsearch.widgets.configure({
              attributesToHighlight: [],
              attributesToRetrieve: ['query'],
              filters: '',
              hitsPerPage: 6,
            }),
            relatedResults({
              container: this.querySelector('[data-source-section="searches"]'),
              type: 'search',
              transformItems: (items, { results }) =>
                items.map((item) => ({
                  ...item,
                  queryID: item.__queryID,
                  position: item.__position,
                  index: results.index,
                })),
            }),
          ]),
      ]);

      // Collections
      this.search.addWidgets([
        window.instantsearch.widgets
          .index({
            indexName: `${this.algoliaConfig.index_prefix}collections`,
          })
          .addWidgets([
            window.instantsearch.widgets.configure({
              attributesToHighlight: [],
              attributesToRetrieve: ['handle', 'title'],
              filters: '',
              hitsPerPage: 6,
            }),
            relatedResults({
              container: this.querySelector('[data-source-section="collections"]'),
              type: 'collection',
              transformItems: (items, { results }) =>
                items.map((item) => ({
                  ...item,
                  queryID: item.__queryID,
                  position: item.__position,
                  index: results.index,
                })),
            }),
          ]),
      ]);

      // Articles
      this.search.addWidgets([
        window.instantsearch.widgets
          .index({
            indexName: `${this.algoliaConfig.index_prefix}articles`,
          })
          .addWidgets([
            window.instantsearch.widgets.configure({
              attributesToHighlight: [],
              attributesToRetrieve: ['blog.handle', 'handle', 'title'],
              filters: '',
              hitsPerPage: 3,
            }),
            relatedResults({
              container: this.querySelector('[data-source-section="articles"]'),
              type: 'article',
              transformItems: (items, { results }) =>
                items.map((item) => ({
                  ...item,
                  queryID: item.__queryID,
                  position: item.__position,
                  index: results.index,
                })),
            }),
          ]),
      ]);

      // Promo Banner
      this.search.addWidgets([
        banners({
          container: this.querySelector('.search-dropdown__content-banner'),
        }),
      ]);
    }

    saveSections() {
      // Loop over each section and store them
      this.sectionIds.forEach((section) => {
        this.sections[section] = this.querySelector(`[data-source-section="${section}"]`).innerHTML;
      });
    }

    reset() {
      // Loop over each section and insert the previously stored section
      this.sectionIds.forEach((section) => {
        this.querySelector(`[data-source-section="${section}"]`).innerHTML = this.sections[section];
      });

      // Remove 'View All Products' button
      this.querySelector('.search-dropdown__view-all-btn').remove();

      // Remove banners
      this.querySelector('.search-dropdown__content-banner').innerHTML = '';

      // Reattach event listeners
      const allProductCards = this.querySelectorAll('[data-product-card]');

      allProductCards.forEach((card) => {
        // Card hover events
        const hoverElem = card.querySelector('[data-product-card-inner]');

        hoverElem.removeEventListener('mouseenter', handleCardHover);
        hoverElem.addEventListener('mouseenter', handleCardHover, {
          once: true,
        });

        // Swatches click events
        const cardSwatches = card.querySelectorAll('[data-product-card-color-id]');

        cardSwatches.forEach((swatch) => {
          swatch.removeEventListener('click', handleSwatchClick);
          swatch.addEventListener('click', handleSwatchClick);
        });

        if (card.querySelector('[data-show-remaining-colours]')) {
          card.querySelector('[data-show-remaining-colours]').addEventListener('click', (e) => {
            e.target.remove();

            card.querySelectorAll('.product-card-colors__link--hidden').forEach((el) => {
              el.classList.remove('product-card-colors__link--hidden');
            });
          });
        }
      });
    }

    /**
     * Readjust panel styles
     * @description Adjust the search dropdown style depending on viewport size.
     */
    readjust() {
      const headerRect = this.siteHeader.getBoundingClientRect();
      const innerHeaderDesktop = this.siteHeader.querySelector('collapsible-header');
      const innerHeaderDesktopRect = innerHeaderDesktop.getBoundingClientRect();
      const zIndex = getComputedStyle(document.documentElement).getPropertyValue('--z-index-sticky');

      if (window.innerWidth < 1024) {
        this.style.top = `${headerRect.bottom}px`;
        this.style.zIndex = parseInt(zIndex) + 10;
      } else {
        this.style.top = `${innerHeaderDesktopRect.top + 1}px`;
        this.style.zIndex = parseInt(zIndex) + 1;
      }
    }

    open() {
      this.readjust();

      this.visibility = true;
    }

    close() {
      this.visibility = false;

      // Reset input
      this.headerSearchInput.value = '';
      this.headerSearchResetBtn.classList.add('visually-hidden');

      // Reset sections
      this.reset();
    }

    get visibility() {
      return this._visibility;
    }

    set visibility(val) {
      this._visibility = val;

      // Toggle visibility of dropdown
      this.classList.toggle('hidden', !val);

      // Toggle scroll on page
      document.body.classList.toggle('overflow-hidden', val);
    }
  }
  if (!customElements.get('search-dropdown')) {
    customElements.define('search-dropdown', SearchDropdown);
  }
})();
